const ADSGRAM_STORAGE_KEY = "adsgramTracking";
const VALID_LABELS = ["ladder", "referral", "blockchain", "jackpot"] as const;

type AdsgramLabel = (typeof VALID_LABELS)[number];

export interface AdsgramTracking {
  label: AdsgramLabel;
  campaign_id: string;
  banner_id: string;
  publisher_id: string;
  click_id: string;
}

type TelegramWindow = Window & {
  Telegram?: {
    WebApp?: {
      initDataUnsafe?: {
        start_param?: string;
        user?: {
          id?: number;
        };
      };
    };
  };
};

function parseAdsgramPayload(value: string | null | undefined): AdsgramTracking | null {
  if (!value) return null;
  const parts = value.split("_");
  if (parts.length !== 5) return null;
  const [label, campaign_id, banner_id, publisher_id, click_id] = parts;
  if (!(VALID_LABELS as readonly string[]).includes(label)) return null;
  if (!campaign_id || !banner_id || !publisher_id || !click_id) return null;
  return { label: label as AdsgramLabel, campaign_id, banner_id, publisher_id, click_id };
}

export function persistAdsgramTrackingFromEntry(): AdsgramTracking | null {
  if (typeof window === "undefined") return null;

  try {
    // Never overwrite existing tracking — save only on first visit
    if (window.localStorage.getItem(ADSGRAM_STORAGE_KEY)) return null;
  } catch {
    return null;
  }

  const tg = (window as TelegramWindow).Telegram?.WebApp?.initDataUnsafe;
  const searchParams = new URLSearchParams(window.location.search);

  const candidate =
    tg?.start_param ??
    searchParams.get("startapp") ??
    searchParams.get("start") ??
    searchParams.get("tgWebAppStartParam");

  const tracking = parseAdsgramPayload(candidate);
  if (!tracking) return null;

  try {
    window.localStorage.setItem(ADSGRAM_STORAGE_KEY, JSON.stringify(tracking));
    console.log("[Adsgram] click stored:", tracking.click_id);
  } catch {
    return null;
  }

  return tracking;
}

export function getStoredAdsgramTracking(): AdsgramTracking | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ADSGRAM_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdsgramTracking;
  } catch {
    return null;
  }
}

export async function sendAdsgramConversion(): Promise<void> {
  const tracking = getStoredAdsgramTracking();
  if (!tracking?.click_id) return;

  const tgid = (window as TelegramWindow).Telegram?.WebApp?.initDataUnsafe?.user?.id;
  if (!tgid) return;

  const token = process.env.REACT_APP_ADSGRAM_TOKEN;
  if (!token) {
    console.warn("[Adsgram] REACT_APP_ADSGRAM_TOKEN not configured");
    return;
  }

  const url = new URL("https://api.adsgram.ai/confirm_conversion");
  url.searchParams.set("token", token);
  url.searchParams.set("tgid", String(tgid));
  url.searchParams.set("campaignid", tracking.campaign_id);
  url.searchParams.set("goaltype", "2");

  try {
    const response = await fetch(url.toString());
    if (response.ok) {
      console.log("[Adsgram] conversion sent for click_id:", tracking.click_id);
    } else {
      console.error("[Adsgram] conversion failed:", response.status, await response.text());
    }
  } catch (error) {
    console.error("[Adsgram] conversion failed:", error);
  }
}
